/**
 * TanStack Query hooks for `creator-drilldown`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	fetchSummary,
	fetchTimeline,
	logEvent,
	creatorDrilldownQueryKeys,
	type BucketKind,
	type EventRow,
	type LogEventBody,
	type SummaryResponse,
	type TimelineResponse,
} from "./api";

export function useCreatorSummary(input: {
	influencerId: string;
	from: number;
	to: number;
	enabled?: boolean;
}): UseQueryResult<SummaryResponse, Error> {
	return useQuery({
		queryKey: creatorDrilldownQueryKeys.summary(
			input.influencerId,
			input.from,
			input.to,
		),
		queryFn: () =>
			fetchSummary({
				influencerId: input.influencerId,
				from: input.from,
				to: input.to,
			}),
		enabled:
			input.enabled !== false &&
			input.influencerId.length > 0 &&
			input.from <= input.to,
	});
}

export function useCreatorTimeline(input: {
	influencerId: string;
	from: number;
	to: number;
	bucket?: BucketKind;
	enabled?: boolean;
}): UseQueryResult<TimelineResponse, Error> {
	const bucket: BucketKind = input.bucket ?? "day";
	return useQuery({
		queryKey: creatorDrilldownQueryKeys.timeline(
			input.influencerId,
			input.from,
			input.to,
			bucket,
		),
		queryFn: () =>
			fetchTimeline({
				influencerId: input.influencerId,
				from: input.from,
				to: input.to,
				bucket,
			}),
		enabled:
			input.enabled !== false &&
			input.influencerId.length > 0 &&
			input.from <= input.to,
	});
}

export function useLogCreatorEvent(): UseMutationResult<
	{ event: EventRow },
	Error,
	LogEventBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: logEvent,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: creatorDrilldownQueryKeys.all });
		},
	});
}
